import React, { useEffect, useState } from "react";
import config from "../config.js";
import { isTm, isTmFinal } from "../utils/tmPricing";

const API_BASE = config.API_BASE_URL;

/**
 * Shared modal for allocating surplus or orphan parts from a source job to another
 * Sales Order. Used by both JobEntryPage (during job creation / batch staging) and
 * SalesOrderPage (for existing "Overtracked" line items).
 *
 * Props:
 *   show                  - boolean, modal visibility
 *   onClose               - callback when user cancels / closes
 *   context               - allocation context, shape:
 *                             { part_id, part_product_id, part_description,
 *                               surplus (for same-line) | unallocated (for orphan),
 *                               source_so_line_id (null for orphan),
 *                               part_customer_id, part_customer_name (orphan only) }
 *   currentSoId           - the SO the user is currently on (excluded from target list)
 *   sourceCustomerId      - the customer of the source SO; used to default + detect
 *                           cross-customer allocations
 *   customerList          - array of { customer_id, name } for the customer selector
 *   onConfirm             - callback invoked with an array of allocation objects on
 *                           confirm. Caller decides whether to store them as pending
 *                           or POST immediately.
 */
export default function SurplusAllocationModal({
    show,
    onClose,
    context,
    currentSoId,
    sourceCustomerId,
    customerList,
    onConfirm
}) {
    const [selectedTargetCustomerId, setSelectedTargetCustomerId] = useState(null);
    const [targetSOs, setTargetSOs] = useState([]);
    const [allocations, setAllocations] = useState({});
    const [loading, setLoading] = useState(false);
    // Time & Materials orders the target customer has open that cannot take THESE pieces, each
    // {order_number, why}: 'line' = surplus of a line (a T&M order takes only a shared sheet's
    // pieces of a part that is not a line of the source order), 'priced' = its pricing is approved.
    // A PENDING T&M order IS offered for orphan (shared-sheet) pieces, and then must receive every
    // one of them (it pays for that part's share of the sheet; the API refuses a split).
    const [tmSkipped, setTmSkipped] = useState([]);

    const isOrphan = !!(context && context.source_so_line_id == null);
    // For orphan parts, total to allocate comes from unallocated; for surplus, from surplus.
    const totalToAllocate = context
        ? (isOrphan ? (context.unallocated || 0) : (context.surplus || 0))
        : 0;

    // Auto-pick default target customer whenever the modal opens:
    //  - Orphan: use the part's owning customer (parts.customer_id)
    //  - Surplus: stay with the source customer (existing behavior)
    useEffect(() => {
        if (!show || !context) return;
        const defaultCustId = isOrphan
            ? (context.part_customer_id != null ? parseInt(context.part_customer_id) : null)
            : (sourceCustomerId != null ? parseInt(sourceCustomerId) : null);
        setSelectedTargetCustomerId(defaultCustId || null);
        setTargetSOs([]);
        setAllocations({});
    }, [show, context, isOrphan, sourceCustomerId]);

    // Load open SOs for the selected target customer, then fetch truth for each and
    // find lines that have this part with remaining quantity.
    useEffect(() => {
        if (!show || !selectedTargetCustomerId || !context) {
            setTargetSOs([]);
            setTmSkipped([]);
            return;
        }
        setLoading(true);
        setTmSkipped([]);
        // A late answer for a customer that is no longer selected must not overwrite this one.
        let alive = true;

        fetch(`${API_BASE}/sales_orders?customer_id=${selectedTargetCustomerId}&status=Open`)
            .then(r => r.json())
            .then(async (orders) => {
                const candidates = [];
                const skippedTm = [];
                for (const so of orders) {
                    if (!alive) return;
                    if (so.sales_order_id === parseInt(currentSoId)) continue;
                    const soIsTm = isTm(so);
                    if (soIsTm && (!isOrphan || isTmFinal(so))) {
                        skippedTm.push({ order_number: so.order_number, why: isTmFinal(so) ? 'priced' : 'line' });
                        continue;
                    }
                    try {
                        const truthRes = await fetch(`${API_BASE}/sales_orders/${so.sales_order_id}/truth`);
                        if (!truthRes.ok) continue;
                        const truth = await truthRes.json();
                        const allLines = (truth.lines || []).flatMap(item => {
                            if (item.type === 'item') return [item.data];
                            if (item.type === 'assembly_group') return item.components || [];
                            return [];
                        });
                        // A part on several lines of this target SO: /truth splits its
                        // shipped and in-production across them (so_line_id order,
                        // shipped first, then production up to each line's ordered,
                        // the rest on the last line), so each line's own figures give
                        // its real unmet demand as "remaining".
                        const partLines = allLines.filter(l => l.part_id === context.part_id);
                        // Orphan pieces (a shared sheet's part that is no line here) have nowhere else to go: an order
                        // whose yield policy allows over-ship may take more than its remaining (Faith's Min + Extras
                        // took all 79 FAB311161 off sheet #1400 for 78 still needed). Line surplus keeps the cap.
                        const overShip = isOrphan && !!(truth.header && truth.header.allow_over_ship);
                        const candidateLines = [];
                        if (partLines.length > 0) {
                            partLines.forEach((l) => {
                                const ordered = l.quantity_ordered || 0;
                                const dShip = l.qty_shipped || 0;
                                const dProd = l.qty_in_production || 0;
                                // A negative in-production figure counts as 0, as POST /surplus_allocations
                                // counts it: a line never needs more than ordered − shipped (SO-1106
                                // PERFTABLEPART2B read -1 with 2 of 2 shipped, and 1 was offered the API refused).
                                const remaining = Math.max(0, ordered - dShip - Math.max(0, dProd));
                                // A T&M line takes every piece whatever is left on it (the customer pays
                                // for every good piece: Ship Any Qty).
                                if (remaining > 0 || soIsTm || overShip) {
                                    candidateLines.push({
                                        so_line_id: l.sales_order_line_id,
                                        quantity_ordered: ordered,
                                        qty_in_production: dProd,
                                        qty_shipped: dShip,
                                        remaining,
                                        tm: soIsTm,
                                        overShip,
                                    });
                                }
                            });
                        }
                        if (candidateLines.length > 0) {
                            candidates.push({
                                sales_order_id: so.sales_order_id,
                                order_number: so.order_number,
                                customer_po: so.customer_po,
                                customer_so: so.customer_so,
                                tm: soIsTm,
                                lines: candidateLines,
                            });
                        }
                    } catch (err) {
                        console.warn(`Failed to fetch truth for SO ${so.sales_order_id}:`, err);
                    }
                }
                if (!alive) return;
                setTargetSOs(candidates);
                setTmSkipped(skippedTm);
                setLoading(false);
            })
            .catch(err => {
                if (!alive) return;
                console.error("Error loading target SOs:", err);
                setLoading(false);
            });
        return () => { alive = false; };
    }, [show, selectedTargetCustomerId, currentSoId, context, isOrphan]);

    if (!show || !context) return null;

    const handleQtyChange = (soId, soLineId, val, maxQty) => {
        const key = `${soId}_${soLineId}`;
        const requested = parseInt(val) || 0;
        const clamped = Math.min(Math.max(0, requested), Math.max(0, maxQty));
        setAllocations(prev => ({ ...prev, [key]: clamped }));
    };

    const totalAllocated = Object.values(allocations).reduce((s, v) => s + v, 0);
    const isOverAllocated = totalAllocated > totalToAllocate;
    // Shared sheets: pieces going to a T&M order must ALL go there (one order, every piece).
    const tmAllocated = targetSOs.filter(so => so.tm).reduce((sum, so) => sum + so.lines.reduce(
        (t, line) => t + (allocations[`${so.sales_order_id}_${line.so_line_id}`] || 0), 0), 0);
    const tmOrdersUsed = targetSOs.filter(so => so.tm && so.lines.some(
        line => (allocations[`${so.sales_order_id}_${line.so_line_id}`] || 0) > 0));
    const tmSplit = tmAllocated > 0 && (tmAllocated !== totalToAllocate || totalAllocated !== tmAllocated
        || tmOrdersUsed.length > 1);

    const handleConfirm = () => {
        const allocs = [];
        for (const so of targetSOs) {
            for (const line of so.lines) {
                const key = `${so.sales_order_id}_${line.so_line_id}`;
                const qty = allocations[key] || 0;
                if (qty > 0) {
                    allocs.push({
                        part_id: context.part_id,
                        // null for orphan — backend treats this as an orphan allocation
                        source_so_line_id: context.source_so_line_id != null ? context.source_so_line_id : null,
                        target_so_id: so.sales_order_id,
                        target_so_line_id: line.so_line_id,
                        quantity: qty,
                        // Display info for the pending list
                        target_so_number: so.order_number,
                        target_po: so.customer_po,
                        target_cust_so: so.customer_so
                    });
                }
            }
        }
        onConfirm(allocs);
        onClose();
    };

    const isCrossCustomer = sourceCustomerId != null
        && selectedTargetCustomerId != null
        && parseInt(selectedTargetCustomerId) !== parseInt(sourceCustomerId);

    return (
        <div className="modal d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
            <div className="modal-dialog modal-lg">
                <div className="modal-content">
                    <div className="modal-header bg-warning-subtle">
                        <h5 className="modal-title">
                            <i className="bi bi-arrow-right-circle me-2"></i>
                            {isOrphan ? 'Allocate Orphan Part' : 'Allocate Surplus'}: {context.part_product_id}
                        </h5>
                        <button type="button" className="btn-close" onClick={onClose}></button>
                    </div>
                    <div className="modal-body">
                        <p className="text-muted mb-3">
                            <strong>{totalToAllocate}</strong>{' '}
                            {isOrphan ? 'orphan' : 'surplus'} parts of{' '}
                            <strong>{context.part_product_id}</strong>
                            {context.part_description ? ` (${context.part_description})` : ''}
                            {isOrphan && context.part_customer_name && (
                                <>. Owning customer: <strong>{context.part_customer_name}</strong></>
                            )}
                            .
                        </p>

                        {/* Customer selector — defaults to owning customer (orphan) or source customer (surplus) */}
                        <div className="mb-3">
                            <label className="form-label">Target Customer</label>
                            <select
                                className="form-select"
                                value={selectedTargetCustomerId || ''}
                                onChange={e => setSelectedTargetCustomerId(parseInt(e.target.value) || null)}
                            >
                                <option value="">-- Select Customer --</option>
                                {(customerList || []).map(c => (
                                    <option key={c.customer_id} value={c.customer_id}>{c.name}</option>
                                ))}
                            </select>
                            {isCrossCustomer && (
                                <div className="alert alert-info mt-2 mb-0 py-2">
                                    <small>
                                        <i className="bi bi-info-circle me-1"></i>
                                        Cross-customer allocation: parts from the current job will be
                                        shipped and invoiced against the selected customer's Sales Order.
                                    </small>
                                </div>
                            )}
                        </div>

                        {!selectedTargetCustomerId ? (
                            <div className="alert alert-secondary mb-0">
                                Select a target customer to load eligible Sales Orders.
                            </div>
                        ) : loading ? (
                            <div className="text-center py-3">
                                <div className="spinner-border spinner-border-sm text-primary"></div>
                                <span className="ms-2">Loading eligible Sales Orders...</span>
                            </div>
                        ) : targetSOs.length === 0 ? (
                            <div className="alert alert-info mb-0">
                                No other open Sales Orders found for this customer with {context.part_product_id}.
                            </div>
                        ) : (
                            <table className="table table-bordered table-sm align-middle">
                                <thead className="table-light">
                                    <tr>
                                        <th>Sales Order</th>
                                        <th>PO</th>
                                        <th>Cust SO</th>
                                        <th className="text-center">Ordered</th>
                                        <th className="text-center">Remaining</th>
                                        <th className="text-center" style={{ width: '120px' }}>Allocate</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {targetSOs.map(so => so.lines.map(line => {
                                        const key = `${so.sales_order_id}_${line.so_line_id}`;
                                        const free = totalToAllocate - totalAllocated + (allocations[key] || 0);
                                        const maxQty = Math.max(0, (so.tm || line.overShip) ? free : Math.min(free, line.remaining));
                                        return (
                                            <tr key={key}>
                                                <td className="fw-bold">
                                                    {so.order_number}
                                                    {so.tm && <span className="badge text-bg-info ms-2" title="Time & Materials order">T&amp;M</span>}
                                                </td>
                                                <td>{so.customer_po || '-'}</td>
                                                <td>{so.customer_so || '-'}</td>
                                                <td className="text-center">{line.quantity_ordered}</td>
                                                <td className="text-center" title={line.overShip && !so.tm ? 'This order allows over-ship: it can take more than its remaining' : undefined}>
                                                    {line.remaining}
                                                    {line.overShip && !so.tm && (allocations[key] || 0) > line.remaining && (
                                                        <div className="small text-muted" data-testid="over-ship-note">+{(allocations[key] || 0) - line.remaining} over (policy allows)</div>
                                                    )}
                                                </td>
                                                <td className="text-center">
                                                    <div className="d-flex gap-1 align-items-center justify-content-center">
                                                        <input
                                                            type="number"
                                                            className="form-control form-control-sm text-center"
                                                            min={0}
                                                            max={maxQty}
                                                            value={allocations[key] ?? ''}
                                                            onChange={e => handleQtyChange(so.sales_order_id, line.so_line_id, e.target.value, maxQty)}
                                                            placeholder="0"
                                                            style={{ minWidth: '60px' }}
                                                        />
                                                        <button
                                                            type="button"
                                                            className="btn btn-sm btn-outline-primary"
                                                            onClick={() => handleQtyChange(so.sales_order_id, line.so_line_id, String(maxQty), maxQty)}
                                                            disabled={maxQty === 0 || (allocations[key] || 0) === maxQty}
                                                            title={`Allocate all ${maxQty} available to this line`}
                                                        >
                                                            All
                                                        </button>
                                                    </div>
                                                </td>
                                            </tr>
                                        );
                                    }))}
                                </tbody>
                            </table>
                        )}

                        {targetSOs.some(so => so.tm) && (
                            <div className="alert alert-info mt-2 mb-0 py-2 small" data-testid="tm-target-note">
                                <i className="bi bi-calculator me-1"></i>
                                {targetSOs.filter(so => so.tm).map(so => so.order_number).join(', ')}{' '}
                                {targetSOs.filter(so => so.tm).length > 1 ? 'are' : 'is'} Time &amp; Materials: allocate{' '}
                                <strong>all {totalToAllocate}</strong> pieces of {context.part_product_id} to it. It
                                pays for this part's share of the sheet (laser time and plate, any scrap included), so
                                its pieces can't be split with another order.
                            </div>
                        )}

                        {tmSplit && (
                            <div className="alert alert-warning mt-2 mb-0 py-2 small" data-testid="tm-split-warning">
                                <i className="bi bi-exclamation-triangle me-1"></i>
                                A Time &amp; Materials order must receive every piece ({totalToAllocate}) and no other
                                order can share them. Put all {totalToAllocate} on one T&amp;M line.
                            </div>
                        )}

                        {tmSkipped.length > 0 && (
                            <div className="alert alert-secondary mt-2 mb-0 py-2 small">
                                <i className="bi bi-calculator me-1"></i>
                                {tmSkipped.some(t => t.why === 'line') && (
                                    <div>
                                        Not listed: {tmSkipped.filter(t => t.why === 'line').map(t => t.order_number).join(', ')}
                                        {' '}— Time &amp; Materials orders can't receive surplus of another order's line
                                        (only a shared sheet's pieces of a part that isn't a line of this order).
                                    </div>
                                )}
                                {tmSkipped.some(t => t.why === 'priced') && (
                                    <div>
                                        Not listed: {tmSkipped.filter(t => t.why === 'priced').map(t => t.order_number).join(', ')}
                                        {' '}— Time &amp; Materials pricing is already approved (reopen it to add pieces).
                                    </div>
                                )}
                            </div>
                        )}

                        {totalAllocated > 0 && (
                            <div className={`alert ${isOverAllocated ? 'alert-danger' : 'alert-success'} mt-2 mb-0`}>
                                <strong>{totalAllocated}</strong> of {totalToAllocate}{' '}
                                {isOrphan ? 'orphan' : 'surplus'} parts allocated.
                                {isOverAllocated && (
                                    <span className="ms-2">
                                        <i className="bi bi-exclamation-triangle me-1"></i>
                                        Over-allocated by {totalAllocated - totalToAllocate} — reduce before confirming.
                                    </span>
                                )}
                                {!isOverAllocated && totalAllocated < totalToAllocate && (
                                    <span className="text-muted ms-2">({totalToAllocate - totalAllocated} unallocated)</span>
                                )}
                            </div>
                        )}
                    </div>
                    <div className="modal-footer">
                        <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
                        <button
                            className="btn btn-warning"
                            onClick={handleConfirm}
                            disabled={totalAllocated === 0 || isOverAllocated || tmSplit}
                        >
                            <i className="bi bi-check-lg me-1"></i>
                            Confirm Allocation ({totalAllocated} parts)
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}
